import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';

const ReportCandidate = ({ candidate, show, onHide }) => {
  const [reportData, setReportData] = useState({
    email: '',
    reason: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [hasCheckedReport, setHasCheckedReport] = useState(false);

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user')) || null;

  // Check if candidate is already reported when modal is about to open
  useEffect(() => {
    const checkAndShowForm = async () => {
      // Don't check again if we've already checked and shown the form
      if (hasCheckedReport) return;

      if (show && currentUser?.id && candidate?.user_id) {
        try {
          const reportResponse = await fetch(`${BaseUrl}/api/candidatereport`);
          const reportData = await reportResponse.json();

          if (reportResponse.ok) {
            const hasReported = reportData.some(
              report => report.emp_id === currentUser.id && report.candidate_id === parseInt(candidate.user_id)
            );

            if (hasReported) {
              // Already reported - show alert and don't open modal
              onHide(); // Close the modal
              Swal.fire({
                icon: 'info',
                title: 'Already Reported',
                text: 'This candidate is already reported by you.',
                confirmButtonColor: '#3085d6'
              });
            } else {
              // Not reported - show the form and mark as checked
              setShowForm(true);
              setHasCheckedReport(true);
            }
          }
        } catch (error) {
          console.error('Error checking reports:', error);
          // If error, still show the form and mark as checked
          setShowForm(true);
          setHasCheckedReport(true);
        }
      } else if (show) {
        // If no user info but modal should show, show the form and mark as checked
        setShowForm(true);
        setHasCheckedReport(true);
      }
    };

    if (show) {
      checkAndShowForm();
    }
  }, [show, currentUser, candidate, onHide, hasCheckedReport]);

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const payload = {
        emp_id: currentUser.id,
        first_name: currentUser.first_name || 'Anonymous',
        candidate_id: candidate?.user_id || candidate?.id || candidate?.job_seeker_id || candidate?.job_seeker_uid || null,
        candidate_name: candidate.first_name,
        email: reportData.email || currentUser.email,
        reason: reportData.reason,
        description: reportData.description
      };

      const response = await fetch(`${BaseUrl}/api/candidatereport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Close loading alert and show success
        Swal.fire({
          icon: 'success',
          title: 'Report Submitted Successfully!',
          text: 'Thank you for reporting this candidate. Our team will review it shortly.',
          confirmButtonColor: '#28a745',
          timer: 3000,
          showConfirmButton: true
        }).then(() => {
          handleClose();
        });
      } else {
        const data = await response.json();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.error || 'Failed to submit report',
          confirmButtonColor: '#d33'
        });
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while submitting your report',
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReportData({
      email: currentUser?.email || '',
      reason: '',
      description: ''
    });
    setShowForm(false);
    setHasCheckedReport(false); // Reset the check flag when closing
    onHide();
  };

  // Set email from current user when form is about to show
  useEffect(() => {
    if (showForm && currentUser?.email) {
      setReportData(prev => prev.email === currentUser.email ? prev : ({ ...prev, email: currentUser.email }));
    }
  }, [showForm, currentUser?.email]);

  return (
    <Modal show={showForm} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Report Candidate</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Your email address</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={reportData.email}
              onChange={handleReportChange}
              placeholder="Enter your email"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Reason for reporting Candidate</Form.Label>
            <Form.Select
              name="reason"
              value={reportData.reason}
              onChange={handleReportChange}
              required
            >
              <option value="">Please select</option>
              <option value="Fraudulent">Fraudulent</option>
              <option value="Misleading">Misleading</option>
              <option value="Discriminatory">Discriminatory</option>
              <option value="Other">Other</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Please describe your issues with this candidate</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={reportData.description}
              onChange={handleReportChange}
              rows={4}
              placeholder="Provide details about why you are reporting this candidate..."
              required
            />
          </Form.Group>

          <div className="alert alert-warning mb-3">
            ⚠️ To help fast track investigation, please include any relevant details that prompted you to report this candidate.
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Report Candidate'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ReportCandidate;